import { apiService, ApiResponse } from './api.service';
import { API_URL } from '../config/api.config';

export interface CustomerType {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  isSubscriptionBased: boolean;
  numberPrefix: string;
  nextNumber: number;
  paddingLength: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { customers: number; plans: number };
}

export interface CreateCustomerTypeDto {
  name: string;
  description?: string;
  isSubscriptionBased?: boolean;
  numberPrefix?: string;
  paddingLength?: number;
}

export interface UpdateCustomerTypeDto {
  name?: string;
  description?: string;
  isSubscriptionBased?: boolean;
  numberPrefix?: string;
  paddingLength?: number;
}

class CustomerTypesService {
  async getAll(tenantId?: string): Promise<ApiResponse<CustomerType[]>> {
    if (tenantId) {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/customer-types`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Tenant-Id': tenantId,
        },
      });

      if (!response.ok) {
        let message = `Failed to fetch customer types: ${response.status}`;
        try {
          const err = await response.json();
          message = err?.message || message;
        } catch {
          // Keep default error message when response body is not JSON.
        }
        throw new Error(message);
      }

      const payload = await response.json();
      return {
        success: true,
        data: payload?.data ?? payload,
      };
    }

    return apiService.get<CustomerType[]>('/customer-types');
  }

  async getById(id: string): Promise<ApiResponse<CustomerType>> {
    return apiService.get<CustomerType>(`/customer-types/${id}`);
  }

  async create(data: CreateCustomerTypeDto): Promise<ApiResponse<CustomerType>> {
    return apiService.post<CustomerType>('/customer-types', data);
  }

  async update(id: string, data: UpdateCustomerTypeDto): Promise<ApiResponse<CustomerType>> {
    return apiService.patch<CustomerType>(`/customer-types/${id}`, data);
  }

  async remove(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiService.delete<{ message: string }>(`/customer-types/${id}`);
  }
}

export const customerTypesService = new CustomerTypesService();
